import { TextButton } from '@creditornot/cb-button';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ITEM_MAX_IMAGE_COUNT } from 'modules/menu/constants';
import { objectId } from 'modules/menu/options/utils';
import { retrieveImageIdFromMultiImageValue } from 'modules/menu/utils';
import {
  ImageGalleryBrowseOverlayHandler,
  ImageGalleryBrowseOverlay,
} from 'modules/shared-menu/pages/ListingManagerImageGalleryPage/components/ImageGalleryBrowseOverlay';

import ExistingImage from './ExistingImage';
import MultiImageDndContext from './MultiImageDndContext';
import MultiImageFileSelector, { MultiImageFileError } from './MultiImageFileSelector';
import NewImage from './NewImage';
import UploadErrorsAlert from './UploadErrorsAlert';
import { Crop } from '../ImageCropper';

export type Props = {
  className?: string;
  imageCountLimit: number;
  onChange: (value: MultiImageValue[]) => void;
  value: MultiImageValue[];
  showImageGalleryButton?: boolean;
};

export type MultiImageValue =
  | {
      id: string;
      type: 'existing-image';
      imageUrl: string;
      downloadUrl?: string;
    }
  | {
      id: string;
      type: 'new-image';
      file: File;
      crop?: Crop;
    }
  | {
      id: string;
      type: 'image-gallery-image';
      imageUrl: string;
    };

const Root = styled.div``;

const ImageGalleryButton = styled(TextButton)`
  margin-block-start: 8px;
`;

const MultiImageUploadInput = ({ className, imageCountLimit, onChange, value, showImageGalleryButton }: Props) => {
  const [cropping, setCropping] = useState(false);
  const [errors, setErrors] = useState<MultiImageFileError[]>([]);
  const [imageGalleryVisible, setImageGalleryVisible] = useState(false);
  const imageGalleryRef = useRef<ImageGalleryBrowseOverlayHandler>(null);

  const handleSelectFiles = useCallback(
    (files: File[]) => {
      const addedFiles = files.map(file => ({
        id: objectId(),
        file,
        type: 'new-image' as const,
      }));

      onChange([...value, ...addedFiles]);
    },
    [onChange, value],
  );

  const handleRemoveImage = useCallback(
    (imageId: string) => {
      setErrors([]);
      onChange(value.filter(v => v.id !== imageId));
    },
    [onChange, value],
  );

  const handleCropChange = useCallback(
    (crop: Crop, image: MultiImageValue) => {
      onChange(value.map(v => (v.id === image.id ? { ...image, crop } : v)));
      setCropping(false);
    },
    [onChange, value],
  );

  const handleCropStart = useCallback(() => {
    setErrors([]);
    setCropping(true);
  }, []);

  const handleCropCancel = useCallback(() => {
    setErrors([]);
    setCropping(false);
  }, []);

  const handleImageGalleryChange = (selectedImages: { id: string; imageUrl: string }[]) => {
    const addedImages = selectedImages.filter(s => !value.find(v => retrieveImageIdFromMultiImageValue(v) === s.id));
    const removedImagesIds = value
      .filter(v => !selectedImages.find(s => retrieveImageIdFromMultiImageValue(v) === s.id))
      .map(v => v.id);

    if (addedImages.length > 0 || removedImagesIds.length > 0) {
      onChange([
        ...value.filter(v => !removedImagesIds.includes(v.id)),
        ...addedImages.map(img => ({ ...img, type: 'image-gallery-image' }) as MultiImageValue),
      ]);
    }
  };

  const assignedImagesIds = useMemo(() => value.map(retrieveImageIdFromMultiImageValue), [value]);

  return (
    <>
      <Root className={className}>
        <MultiImageDndContext onUpdateImageOrder={onChange} originalList={[...value]}>
          {({ displayImageList }) => {
            return (
              <>
                {displayImageList.map(image =>
                  image.type === 'existing-image' ? (
                    <ExistingImage
                      key={image.id}
                      imageUrl={image.imageUrl}
                      imageId={image.id}
                      interactionsDisabled={cropping === true}
                      downloadUrl={image.downloadUrl}
                      onRemove={handleRemoveImage}
                    />
                  ) : image.type === 'image-gallery-image' ? (
                    <ExistingImage
                      key={image.id}
                      imageUrl={image.imageUrl}
                      imageId={image.id}
                      interactionsDisabled={cropping === true}
                      onRemove={imageGalleryRef.current?.removeSelectionById}
                    />
                  ) : (
                    <NewImage
                      key={image.id}
                      image={image}
                      interactionsDisabled={cropping === true}
                      onCropChange={handleCropChange}
                      onRemove={handleRemoveImage}
                      onStartCropping={handleCropStart}
                      onCancelCropping={handleCropCancel}
                    />
                  ),
                )}
              </>
            );
          }}
        </MultiImageDndContext>
        <MultiImageFileSelector
          imageCountLimit={imageCountLimit}
          currentImageCount={value.length}
          onSelectMultipleFiles={handleSelectFiles}
          onSetErrors={setErrors}
        />
        {errors.length > 0 && <UploadErrorsAlert errors={errors} countLimit={imageCountLimit} />}
      </Root>
      {showImageGalleryButton && (
        <ImageGalleryButton variant="brand" onClick={() => setImageGalleryVisible(true)}>
          <FormattedMessage id="image-upload.multiImage.imageGalleryButtonLabel" />
        </ImageGalleryButton>
      )}
      {createPortal(
        <ImageGalleryBrowseOverlay
          isVisible={imageGalleryVisible}
          onClose={() => setImageGalleryVisible(false)}
          onChange={handleImageGalleryChange}
          ref={imageGalleryRef}
          limit={ITEM_MAX_IMAGE_COUNT}
          assignedImagesIds={assignedImagesIds}
        />,
        document.body,
      )}
    </>
  );
};

export default MultiImageUploadInput;
